import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Alert, Checkbox, Input, Typography } from 'antd'
import { Modal, CenteredSpin } from '../../../ui'
import { encode, btoa } from 'js-base64'
import { ButtonsBlock } from '../../../Edit/components'
import { openAccountService } from '../../../../services/accounts'
import {errorHandler, isExpired, userTypeIdPicker} from '../../../helpers'
import { useAppSelector } from '../../../../store/hooks'
import SignUtils from '../../../../utils/sign/SignUtils'
import { hexToBinary } from '../../../helpers'
import { getAccounts } from '../../../../store/accounts'
import { useGetTariffRate } from '../../../hooks'
import { v4 as uuidv4 } from 'uuid'
import style from './style.m.less'
import {EUserRole, EUserType} from "../../../../constants/approval";
import {accNameMaxLength} from "../OpenAccountModal/OpenAccountModal";

interface IOpenFirstAccountModal {
    isModalVisible: boolean
    setIsModalVisible: (value: boolean) => void
}

const OpenFirstAccountModal: FC<IOpenFirstAccountModal> = ({
    isModalVisible,
    setIsModalVisible,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [name, setName] = useState<string>('')
    const [responseError, setResponseError] = useState<string>('')

    const userInfo = useAppSelector((store) => store.user)
    const { data } = userInfo

    const isClientGOOwnerTSO = data?.user_role == EUserRole.Client
        || data?.user_role == EUserRole.GOOwner
        || data?.user_role == EUserRole.TSO
    const isSignExpired = isClientGOOwnerTSO && isExpired(data?.certificate_data.token_expiration)
    // У пользователя должна быть МЧД
    const needAttorney = data?.user_type === EUserType.UrLico && isClientGOOwnerTSO && !data?.without_attorney
    // У пользователя должна быть МЧД, но её нет
    const isEmptyAttorney = needAttorney && !data?.machine_readable_attorney
    // У пользователя есть МЧД и у неё истёк срок
    const isAttorneyExpired = needAttorney
        && !!data?.machine_readable_attorney
        && isExpired(data?.machine_readable_attorney.validity_period, true)

    const onChangeName = (e: any) => {
        setName(e.target.value)
    }

    const handleCancel = () => {
        setResponseError('')
        setIsModalVisible(false)
    }

    const { loadingTariffs, balance, rate } = useGetTariffRate({
        dependency: isModalVisible,
        id: 10,
    })

    const onFinish = async () => {
        //TODO вынести функционал подписывания в отдельный хук
        if (name !== '') {
            const requestUuid = uuidv4()
            const thumbprint = await SignUtils.getCertificateThumbprint(
                data.certificate_data.certificate,
            )
            const totalInfo = { name }
            const encodedInfo = encode(JSON.stringify(totalInfo))
            const encoded = requestUuid + encodedInfo
            const preSignature = await SignUtils.SignCreate(thumbprint, encoded)
            const signature = btoa(hexToBinary(preSignature))

            await openAccountService({
                userInfo: totalInfo,
                uuid: requestUuid,
                sign: signature,
                onFinish: () => {
                    setIsModalVisible(false)
                    dispatch(getAccounts())
                },
                onError: (error) => {
                    setResponseError(errorHandler(error.err_code))
                },
            })
        } else {
            setResponseError('Введите наименование лицевого счета')
        }
    }

    if (loadingTariffs) {
        return <CenteredSpin />
    }

    return (
        <Modal isModalVisible={isModalVisible}>
            <div className={style.modalWrapper}>
                <div className={style.modalHeader}>{t('myProfile.openPersonalAccount')}</div>
                <Alert
                    message={'Номер счета присваивается автоматически после открытия'}
                    className={style.alert}
                    type="info"
                    showIcon
                />
                <div className={style.modalText}>
                    <div>{t('myProfile.accountName')}</div>
                    <Input
                        className={style.input}
                        maxLength={accNameMaxLength}
                        value={name}
                        onChange={onChangeName}
                    />
                </div>
                <Alert
                    className={style.modalAlert}
                    message={
                        <div className={style.modalAlertText}>
                            <div>
                                За операцию взымается оплата в размере{' '}
                                <Typography.Text strong>{rate}</Typography.Text>
                            </div>
                            <div>Баланс платежного счета: {balance}</div>
                        </div>
                    }
                    type="warning"
                    showIcon
                />
                {responseError && (
                    <Alert
                        className={style.responseError}
                        message={responseError}
                        type="error"
                        showIcon
                    />
                )}
                {isSignExpired && (
                    <Alert
                        className={style.signError}
                        message={'Истек срок действия ЭП'}
                        type="error"
                        showIcon
                    />
                )}
                {(isEmptyAttorney || isAttorneyExpired) && (
                    <Alert
                        className={style.signError}
                        message={isEmptyAttorney ? t('profile.attorneyIsMissing') : t('profile.attorneyIsExpired')}
                        type="error"
                        showIcon
                    />
                )}
                <div className={style.buttonsBlock}>
                    <ButtonsBlock
                        confirmAction={onFinish}
                        cancelAction={handleCancel}
                        confirmTitle={t('myProfile.openAccount')}
                        disableCondition={isSignExpired || isEmptyAttorney || isAttorneyExpired}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default OpenFirstAccountModal
